// "Shop by category" horizontal scroller: desktop arrows appear only when
// the row overflows, and the mobile track grows a thumb that tracks scroll
// position -- both derived from the row's own scrollWidth, not hardcoded
class KludiCategoryBand extends HTMLElement {
  connectedCallback() {
    this.row = this.querySelector('.kludi-category-band__row');
    this.prevButton = this.querySelector('.kludi-category-band__arrow--prev');
    this.nextButton = this.querySelector('.kludi-category-band__arrow--next');
    this.thumb = this.querySelector('.kludi-category-band__thumb');

    this._onScroll = this._onScroll.bind(this);
    this._updateOverflow = this._updateOverflow.bind(this);

    /*
      resize has to redo both halves. the thumb's width is a fraction of the
      row's own scrollWidth, so a viewport change that adds or removes tiles
      per screen makes the old width wrong -- too long or too short depending
      on which way the change went -- and only a scroll would have corrected it
    */
    this._onResize = () => {
      this._updateOverflow();
      this._onScroll();
    };

    this.row.addEventListener('scroll', this._onScroll, { passive: true });
    this.prevButton?.addEventListener('click', () => this._scrollByTile(-1));
    this.nextButton?.addEventListener('click', () => this._scrollByTile(1));

    window.addEventListener('resize', this._onResize);
    this._updateOverflow();
    this._onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
  }

  _updateOverflow() {
    const overflowing = this.row.scrollWidth > this.row.clientWidth + 1;
    this.toggleAttribute('data-overflowing', overflowing);

    /*
      hidden when the row does not overflow at all -- unlike the home page's
      rails, the design draws this band's arrows conditionally, so a band that
      fits shows none. that is the one place this controller and the shared
      rail controller differ on purpose
    */
    if (this.prevButton) this.prevButton.hidden = !overflowing;
    if (this.nextButton) this.nextButton.hidden = !overflowing;

    this._updateArrows(overflowing);
  }

  /*
    each arrow reports its own direction, the same way every other slider on
    this storefront does (assets/kludi-rail.js): greyed at the end of its own
    travel, not only when the whole row fits. an arrow that looks live while
    the row is already at that end is a control that does nothing
  */
  _updateArrows(overflowing) {
    const atStart = this.row.scrollLeft <= 1;
    const atEnd = this.row.scrollLeft >= this.row.scrollWidth - this.row.clientWidth - 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }

  _scrollByTile(direction) {
    const tile = this.row.querySelector('.kludi-category-band__tile');
    if (!tile) return;
    const pitch = tile.getBoundingClientRect().width + 20;
    this.row.scrollBy({ left: direction * pitch, behavior: 'smooth' });
  }

  _onScroll() {
    // the arrows follow the scroll position, not only the initial measurement
    this._updateArrows(this.row.scrollWidth > this.row.clientWidth + 1);

    if (!this.thumb) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.row;
    const maxScroll = scrollWidth - clientWidth;
    const trackWidth = this.thumb.parentElement.clientWidth;
    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 24);
    const maxThumbTravel = trackWidth - thumbWidth;
    const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;

    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transform = `translateX(${progress * maxThumbTravel}px)`;
  }
}

window.customElements.define('kludi-category-band', KludiCategoryBand);
